import { getRandomString } from '@core/utils/random.ts'
import { parseSubscriberStatus, SubscriberStatus, SubscriberStatusStorageValue } from '../enums/subscriber-status'
import {
    type AbstractDataStore,
    DataStoreName,
    StorageType,
    type TimestampedDataValue,
} from '@core/storage/abstract-data-store.ts'
import { Result } from '@core/models/result'
import { AbstractStorageManager } from './abstract-storage-manager'
import { Logger, TaggedLogger } from '@core/logging/logger'
import { LegacyStorageManager } from './legacy-storage-manager'
import { LegacyStorageKey, UserStorageKey } from '@core/storage/keys.ts'
import { OptOutSource } from '../enums/opt-out-source'

/** @internal */
export type UserStore = {
    _synchronizeData(): Promise<void>
    _setSharedStorageReadOnly(): void

    _getAnonymousId(): Promise<string>
    _rotateAnonymousId(): Promise<string | null>
    _getLocale(): Promise<string | null>
    _setLocale(locale: string): Promise<string | null>
    _getTimeZone(): Promise<string | null>
    _setTimeZone(timeZone: string): Promise<string | null>
    _getExternalId(): Promise<string | null>
    _setExternalId(id: string): Promise<boolean>
    _removeExternalId(): Promise<boolean>
    _getIsSubscribed(): Promise<boolean>
    _getSubscriberStatus(): Promise<SubscriberStatus>
    _setSubscriberStatus(status: SubscriberStatus): Promise<boolean>
    _setSubscriberStatusDismissed(expiration: number): Promise<boolean>
    _getSubscriberStatusDismissedExpiration(): Promise<number | null>
    _getIsInDeletedState(): Promise<boolean>
    _setIsInDeletedState(deleted: boolean): Promise<boolean>
    _getIsOptedOut(): Promise<boolean>
    _getOptedOutSource(): Promise<OptOutSource | null>
    _setIsOptedOut(optedOut: true, source: OptOutSource): Promise<boolean>
    _setIsOptedOut(optedOut: false): Promise<boolean>
    _getIsTrackingHalted(): Promise<boolean>
    _haltTrackingForThisPage(): void
    _resumeTrackingForThisPage(): void

    _getReportedSubscriptionFingerprint(): Promise<string | null>
    _setReportedSubscriptionFingerprint(fingerprint: string): Promise<boolean>

    _getLastNotificationPermission(): Promise<NotificationPermission | null>
    _setLastNotificationPermission(permission: NotificationPermission): Promise<boolean>

    _evaluateSubscriberDismissedState(): Promise<void>

    _getIsUserInSoftUnsubscribedState(): Promise<boolean>
    _setIsUserInSoftUnsubscribedState(): Promise<void>
    _unsetIsUserInSoftUnsubscribedState(): Promise<void>

    /**
     * Synchronous access methods.
     * These are for use only with the legacy SDK context.
     */

    /** @deprecated */
    _getAnonymousIdSync(): string
    /** @deprecated */
    _getExternalIdSync(): string | null
    /** @deprecated */
    _getSubscriberStatusSync(): SubscriberStatus
}

const synchronizedKeyStores: [StorageType, ...StorageType[]] = [StorageType.COOKIE, StorageType.IDB, StorageType.MEM]
const durableSynchronizedKeyStores: [StorageType, ...StorageType[]] = [StorageType.COOKIE, StorageType.IDB]
const memoryStoreOnly: [StorageType, ...StorageType[]] = [StorageType.MEM]
const idbAndMemOnlyStores: [StorageType, ...StorageType[]] = [StorageType.IDB, StorageType.MEM]

/** @internal */
export class UserStorageManager extends AbstractStorageManager implements UserStore {
    private _legacyStorage: LegacyStorageManager
    private _isSharedStorageReadOnly = false
    /**
     * Set while the site reports consent pending, or denied with nothing to withdraw.
     * Never persisted: the site reports consent on every page, so storing it would only leave state
     * behind that a later grant has to undo.
     */
    private _isTrackingHaltedForThisPage = false

    /**
     * Gives up this context's ability to write the keys shared across the domain's hosts.
     *
     * The cookie is scoped to the registrable domain, so every host under it writes the same
     * copy, while IndexedDB belongs to one origin alone. An event-only host holds no permission,
     * no worker and no token, so it has nothing to say about who the subscriber is: it reads that
     * state and caches it locally, and the host that owns the subscription remains the only one
     * that can change it. Without this, a host that has simply never learned the subscriber can
     * overwrite the host that has.
     */
    public _setSharedStorageReadOnly(): void {
        this._isSharedStorageReadOnly = true
    }

    /**
     * Where a domain-wide key may be written. Reads always consult every store — a value this
     * context may not write is still a value it must see.
     *
     * Separately from this, a getter that saves a default on a first read writes that default to
     * this origin only. A default records that nothing is known yet, and publishing it to the
     * cookie would put that ignorance in front of every host on the domain, competing with the
     * hosts that do know. Only a value recovered from legacy storage is knowledge worth sharing.
     */
    private get _domainWideKeyWriteStores(): [StorageType, ...StorageType[]] {
        return this._isSharedStorageReadOnly ? idbAndMemOnlyStores : synchronizedKeyStores
    }

    public constructor(legacyStorage: LegacyStorageManager, logger: Logger = TaggedLogger._getInstance('UserStore')) {
        super(DataStoreName.sub, [StorageType.MEM, StorageType.IDB, StorageType.COOKIE, StorageType.SESSION], logger)

        this._legacyStorage = legacyStorage
    }

    public async _synchronizeData() {
        // mem/session is not used during user data synchronization
        const req = this._getAvailableStores([StorageType.IDB, StorageType.COOKIE])
        const availableStores = req.unwrapOr([])

        if (availableStores.length <= 1) {
            this._logger.verbose('[Sync] No available stores to synchronize')
        } else {
            const syncKeys = [
                UserStorageKey.anonymousId,
                UserStorageKey.subscriberStatus,
                UserStorageKey.isInDeletedState,
                // Opting back in moves the marker from 1 to 0, so a host whose IndexedDB still holds
                // an older 1 would otherwise keep it. Sync runs only on a load the halt check let
                // through and keeps the newest value per key, so it only spreads the value the read
                // already chose.
                UserStorageKey.isOptedOut,
                UserStorageKey.DISMISSED_STATUS_EXPIRATION,
            ]

            this._logger.verbose('[Sync] Starting synchronization')
            await Promise.all(syncKeys.map((syncKey) => this._synchronizeKey(syncKey, availableStores)))
            this._logger.verbose('[Sync] Completed synchronization')
        }

        await this._evaluateSubscriberDismissedState()
    }

    /**
     * Brings one key to the same value in every store, keeping the copy that was written last.
     *
     * Arbitration is per key, not per store. The stores hold different subsets of the data — a
     * subdomain has its own IndexedDB while the cookie is shared across the root domain — so a
     * write to one store must not make it authoritative for keys it knows nothing about. Reads
     * already resolve a value this way, so synchronization cannot install a value that a read
     * would have rejected.
     *
     * Per key is as fine-grained as the stores allow, not as fine-grained as it sounds: a cookie
     * holds one timestamp for its whole document, so every key in it reports the time of the last
     * write to any of them. Only IndexedDB stamps a key individually.
     *
     * A store that does not hold the key expresses no opinion: it is written to, never read
     * from. Absence is not a deletion, and a store that has never held a key cannot use that
     * to erase it elsewhere. Removals reach every store through the call that removes the key.
     */
    private async _synchronizeKey(syncKey: UserStorageKey, stores: AbstractDataStore[]): Promise<void> {
        const records = await Promise.all(
            stores.map((store) =>
                store._getData(syncKey).then((res) => ({
                    store,
                    value: res.isOk() ? res.unwrap().value : null,
                    ts: res.isOk() ? res.unwrap().ts : 0,
                })),
            ),
        )

        const held = records.filter((record) => record.value !== null && record.value !== undefined)
        if (!held.length) {
            return
        }

        const winner = this._resolveSynchronizationWinner(syncKey, held)

        await Promise.all(
            records
                .filter((record) => record.store !== winner.store && record.value !== winner.value)
                .filter((record) => !(this._isSharedStorageReadOnly && record.store._storageType === StorageType.COOKIE))
                .map((record) =>
                    record.store._setData(syncKey, winner.value).then((res) => {
                        if (res.isErr()) {
                            this._logger.verbose(
                                `[Sync] Unable to synchronize ${syncKey} value to ${record.store._storageType}. ${
                                    res.getErr().message
                                }`,
                            )
                        } else {
                            this._logger.verbose(`[Sync] Synchronized ${syncKey} value to ${record.store._storageType}`)
                        }
                    }),
                ),
        )
    }

    /**
     * Picks the copy of a key that the other stores should adopt.
     *
     * The most recently written copy wins, with one exception: a subscriber status of
     * `not_determined` records that a store has never learned the status, not that the user has
     * none. Letting it win would turn an empty store into an authority on subscription state and
     * silently unsubscribe a subscriber, so it yields to any store holding a real answer no
     * matter how recently it was written.
     */
    private _resolveSynchronizationWinner<T extends { store: AbstractDataStore; value: any; ts: number }>(
        syncKey: UserStorageKey,
        held: T[],
    ): T {
        const determined =
            syncKey === UserStorageKey.subscriberStatus
                ? held.filter((record) => parseSubscriberStatus(record.value) !== SubscriberStatus.NOT_DETERMINED)
                : held

        const pool = determined.length ? determined : held

        // A context that cannot write the shared store does not get to disagree with it either:
        // reads resolve on the newest timestamp, so leaving a stale local copy in place would
        // let it shadow the shared value it is supposed to be following.
        if (this._isSharedStorageReadOnly) {
            const shared = pool.find((record) => record.store._storageType === StorageType.COOKIE)
            if (shared) {
                return shared
            }
        }

        return pool.reduce((latest, record) => (record.ts > latest.ts ? record : latest))
    }

    public async _evaluateSubscriberDismissedState(): Promise<void> {
        // Cleanup dismissed state
        const synchronizedStatus = await this._getSubscriberStatus()
        const dismissedStateExpiration = await this._getSubscriberStatusDismissedExpiration()

        if (
            synchronizedStatus === SubscriberStatus.DISMISSED &&
            (dismissedStateExpiration === null || new Date().getTime() >= dismissedStateExpiration)
        ) {
            this._logger.info('Removing subscriber dismissed state')
            await Promise.all([
                this._removeData(UserStorageKey.DISMISSED_STATUS_EXPIRATION, {
                    preferredStores: this._domainWideKeyWriteStores,
                }),
                this._setSubscriberStatus(SubscriberStatus.NOT_DETERMINED),
            ])
        } else if (synchronizedStatus !== SubscriberStatus.DISMISSED && dismissedStateExpiration !== null) {
            this._logger.verbose('Removing subscriber dismissed expiration key')
            await this._removeData(UserStorageKey.DISMISSED_STATUS_EXPIRATION, {
                preferredStores: this._domainWideKeyWriteStores,
            })
        }
    }

    /**
     * Automatically saves default-value when no previous value is available
     */
    public async _getAnonymousId(): Promise<string> {
        const req = await this._getData<string>(UserStorageKey.anonymousId, {
            preferredStores: synchronizedKeyStores,
        })

        let piid = req.unwrapOrNull()
        if (!piid) {
            // attempt to recover from legacy store
            const legacy = this._legacyStorage._getAnonymousId()

            const saved = await this._setData(UserStorageKey.anonymousId, legacy ?? getRandomString(), {
                preferredStores: this._domainWideKeyWriteStores,
            })

            if (saved.isOk()) {
                piid = saved.unwrap()
                this._removeLegacyKey(LegacyStorageKey[UserStorageKey.anonymousId])
            }
        }

        return piid!
    }

    public _getAnonymousIdSync(): string {
        let req = this._getDataSync<string>(UserStorageKey.anonymousId, {
            preferredStores: synchronizedKeyStores,
        })

        let piid = req.unwrapOrNull()
        if (!piid) {
            // attempt to recover from legacy store
            const legacy = this._legacyStorage._getAnonymousId()

            const saved = this._setDataSync(UserStorageKey.anonymousId, legacy ?? getRandomString(), {
                preferredStores: this._domainWideKeyWriteStores,
            })

            if (saved.isOk()) {
                piid = saved.unwrap()
            }
        }

        return piid!
    }

    /**
     * Gives this browser a new anonymous id, so nothing it does from now on can be filed under the
     * profile a deletion request has erased. Written to every store the domain shares, even on a host
     * whose shared storage is read-only, for the reason the deletion marker is: a sibling host that
     * kept the old id would go on reporting under it.
     *
     * The legacy copy goes too, because a first read recovers the id from it when the other stores
     * are empty.
     *
     * @returns the new id, or null when neither the cookie nor IndexedDB accepted it.
     */
    public async _rotateAnonymousId(): Promise<string | null> {
        const rotatedAnonymousId = getRandomString()
        const [durableWrite] = await Promise.all([
            this._setData(UserStorageKey.anonymousId, rotatedAnonymousId, {
                preferredStores: durableSynchronizedKeyStores,
            }),
            this._setData(UserStorageKey.anonymousId, rotatedAnonymousId, { preferredStores: memoryStoreOnly }),
        ])
        this._removeLegacyKey(LegacyStorageKey[UserStorageKey.anonymousId])

        return durableWrite.isOk() ? rotatedAnonymousId : null
    }

    public async _getLocale(): Promise<string | null> {
        const req = await this._getData<string>(UserStorageKey.locale, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.unwrapOrNull()
    }

    public async _setLocale(locale: string): Promise<string | null> {
        const req = await this._setData<string>(UserStorageKey.locale, locale, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.unwrapOrNull()
    }

    public async _getTimeZone(): Promise<string | null> {
        const req = await this._getData<string>(UserStorageKey.timeZone, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.unwrapOrNull()
    }

    public async _setTimeZone(timeZone: string): Promise<string | null> {
        const req = await this._setData<string>(UserStorageKey.timeZone, timeZone, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.unwrapOrNull()
    }

    public async _getExternalId(): Promise<string | null> {
        const req = await this._getData<string | null>(UserStorageKey.externalId, {
            preferredStores: idbAndMemOnlyStores,
        })

        let id = req.unwrapOrNull()
        if (!id) {
            const legacyId = this._legacyStorage._getExternalId()
            if (legacyId) {
                const saved = await this._setData(UserStorageKey.externalId, legacyId, {
                    preferredStores: idbAndMemOnlyStores,
                })
                if (saved.isOk()) {
                    id = legacyId
                    this._removeLegacyKey(LegacyStorageKey[UserStorageKey.externalId])
                }
            }
        }

        return id
    }

    public _getExternalIdSync(): string | null {
        const req = this._getDataSync<string | null>(UserStorageKey.externalId, {
            preferredStores: synchronizedKeyStores,
        })

        let id = req.unwrapOrNull()
        if (!id) {
            const legacyId = this._legacyStorage._getExternalId()
            if (legacyId) {
                // External id is never published to the shared cookie - the async accessors keep
                // it on the origin, and legacy storage is itself a root-scoped cookie, so a value
                // recovered here belongs to the whole domain and would otherwise be republished by
                // any host that read it.
                const res = this._setDataSync(UserStorageKey.externalId, legacyId, {
                    preferredStores: idbAndMemOnlyStores,
                })
                if (res.isOk()) {
                    id = legacyId
                }
            }
        }

        return id
    }

    public async _setExternalId(id: string): Promise<boolean> {
        const req = await this._setData<string>(UserStorageKey.externalId, id, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.isOk()
    }

    public async _removeExternalId(): Promise<boolean> {
        const req = await this._removeData(UserStorageKey.externalId, {
            preferredStores: idbAndMemOnlyStores,
        })

        // Removed directly because legacy cleanup through _removeLegacyKey is switched off, and a
        // surviving legacy cookie would be copied back into IndexedDB and memory on the next read.
        try {
            const legacyRemoval = await this._legacyStorage._removeData(LegacyStorageKey[UserStorageKey.externalId])
            if (legacyRemoval?.isErr()) {
                this._logger.warn('Unable to remove the legacy external id', legacyRemoval.getErr()?.message)
            }
        } catch (err) {
            this._logger.warn('Unable to remove the legacy external id', (err as Error)?.message ?? err)
        }
        return req.isOk()
    }

    public async _getIsSubscribed(): Promise<boolean> {
        const status = await this._getSubscriberStatus()
        return status === SubscriberStatus.SUBSCRIBED
    }

    /**
     * Automatically saves default-value when no previous value is available
     */
    public async _getSubscriberStatus(): Promise<SubscriberStatus> {
        const records = await this._mGetTimestampedData<number>(UserStorageKey.subscriberStatus, {
            preferredStores: synchronizedKeyStores,
        })
        let state: SubscriberStatus = SubscriberStatus.NOT_DETERMINED

        const raw = this._resolveStoredSubscriberStatus(records)
        if (raw === null) {
            const legacy = this._legacyStorage._getSubscriberStatus()
            const statusRes = await this._setData(
                UserStorageKey.subscriberStatus,
                SubscriberStatusStorageValue[legacy ?? state],
                {
                    preferredStores: legacy === null ? idbAndMemOnlyStores : this._domainWideKeyWriteStores,
                },
            )

            if (statusRes.isOk()) {
                state = parseSubscriberStatus(statusRes.unwrap())
            }

            if (legacy) {
                this._removeLegacyKey(LegacyStorageKey[UserStorageKey.subscriberStatus])
            }
        } else {
            state = parseSubscriberStatus(raw)
        }

        return state
    }

    /**
     * Picks the stored subscriber status a read should report, or null when no store holds one.
     *
     * Applies the rule synchronization uses: `not_determined` records that a store never learned
     * the status, so a store holding a real answer is preferred however long ago it was written.
     * Reads resolving this differently to synchronization is not a cosmetic inconsistency - a
     * store that synchronization deliberately refused to correct would otherwise go on deciding
     * what every caller sees, which is how a subscriber gets treated as unsubscribed.
     */
    private _resolveStoredSubscriberStatus(records: Record<StorageType, Result<TimestampedDataValue<number>>>) {
        const held = Object.values(records)
            .filter((record) => record.isOk() && record.unwrap().value !== null)
            .map((record) => record.unwrap())

        if (!held.length) {
            return null
        }

        const determined = held.filter(
            (record) => parseSubscriberStatus(record.value) !== SubscriberStatus.NOT_DETERMINED,
        )
        const pool = determined.length ? determined : held

        return pool.reduce((latest, record) => (record.ts > latest.ts ? record : latest)).value
    }

    public async _getIsUserInSoftUnsubscribedState(): Promise<boolean> {
        const req = await this._getData<number>(UserStorageKey.IS_USER_IN_SOFT_UNSUBSCRIBE_STATE, {
            preferredStores: synchronizedKeyStores,
        })
        return !!req.unwrapOrNull()
    }

    public async _setIsUserInSoftUnsubscribedState(): Promise<void> {
        await this._setData<number>(UserStorageKey.IS_USER_IN_SOFT_UNSUBSCRIBE_STATE, 1, {
            preferredStores: this._domainWideKeyWriteStores,
        })
    }

    public async _unsetIsUserInSoftUnsubscribedState(): Promise<void> {
        await this._removeData(UserStorageKey.IS_USER_IN_SOFT_UNSUBSCRIBE_STATE, {
            preferredStores: this._domainWideKeyWriteStores,
        })
    }

    public _getSubscriberStatusSync(): SubscriberStatus {
        const records = this._mGetTimestampedDataSync<number>(UserStorageKey.subscriberStatus, {
            preferredStores: synchronizedKeyStores,
        })
        let state: SubscriberStatus = SubscriberStatus.NOT_DETERMINED

        const raw = this._resolveStoredSubscriberStatus(records)
        if (raw === null) {
            const legacy = this._legacyStorage._getSubscriberStatus()
            const statusRes = this._setDataSync(
                UserStorageKey.subscriberStatus,
                SubscriberStatusStorageValue[legacy ?? state],
                {
                    preferredStores: legacy === null ? idbAndMemOnlyStores : this._domainWideKeyWriteStores,
                },
            )

            if (statusRes.isOk()) {
                state = parseSubscriberStatus(statusRes.unwrap())
            }
        } else {
            state = parseSubscriberStatus(raw)
        }

        return state
    }

    public async _setSubscriberStatus(status: SubscriberStatus): Promise<boolean> {
        const req = await this._setData(UserStorageKey.subscriberStatus, SubscriberStatusStorageValue[status], {
            preferredStores: this._domainWideKeyWriteStores,
        })
        return req.isOk()
    }

    public async _setSubscriberStatusDismissed(expSeconds: number): Promise<boolean> {
        const req = await this._setSubscriberStatus(SubscriberStatus.DISMISSED)
        if (!req) {
            this._logger.warn('Unable to set subscriber dismissed status. Skipping dismissed expiration.')
            return false
        }

        const expReq = await this._setData<number>(
            UserStorageKey.DISMISSED_STATUS_EXPIRATION,
            new Date().getTime() + expSeconds * 1000,
            {
                preferredStores: this._domainWideKeyWriteStores,
            },
        )

        if (!expReq.isOk()) {
            this._logger.warn('Unable to set subscriber dismissed expiration.', expReq.getErr())
        }

        return expReq.isOk()
    }

    public async _getSubscriberStatusDismissedExpiration(): Promise<number | null> {
        const req = await this._getData<number>(UserStorageKey.DISMISSED_STATUS_EXPIRATION, {
            preferredStores: synchronizedKeyStores,
        })

        let expiration = req.unwrapOrNull()
        if (!expiration) {
            const legacyExpiration = await this._legacyStorage._getDismissedStatusExpiration()
            if (legacyExpiration) {
                expiration = legacyExpiration
                await this._setData(UserStorageKey.DISMISSED_STATUS_EXPIRATION, legacyExpiration, {
                    preferredStores: this._domainWideKeyWriteStores,
                })
            }
        }

        return expiration
    }

    public async _getIsInDeletedState(): Promise<boolean> {
        const req = await this._getData<number>(UserStorageKey.isInDeletedState, {
            preferredStores: synchronizedKeyStores,
        })
        let isDeleted = false

        const raw = req.unwrapOrNull()
        if (raw !== null) {
            isDeleted = !!raw
        } else {
            const legacyIsDeleted = this._legacyStorage._getIsInDeletedState()
            const compactValue = legacyIsDeleted ?? isDeleted ? 1 : 0
            const saved = await this._setData(UserStorageKey.isInDeletedState, compactValue, {
                preferredStores: legacyIsDeleted === null ? idbAndMemOnlyStores : this._domainWideKeyWriteStores,
            })

            if (saved.isOk()) {
                isDeleted = !!saved.unwrap()
                this._removeLegacyKey(LegacyStorageKey[UserStorageKey.isInDeletedState])
            }
        }

        return isDeleted
    }

    /**
     * Written to every store the domain shares, even on a host whose shared storage is read-only.
     * That restriction stops a host that cannot know the subscriber from overwriting the host that
     * does. A deletion request is the visitor's own choice, and the host holding the subscription
     * has to see it or it keeps tracking. Under isolated cookies the cookie is per host, so it
     * reaches no other host.
     */
    public async _setIsInDeletedState(deleted: boolean): Promise<boolean> {
        return this._setHaltMarker(UserStorageKey.isInDeletedState, deleted ? 1 : 0)
    }

    /**
     * Whether the visitor withdrew consent in this browser. A first read saves nothing, unlike the
     * deleted state: a stored `0` could only ever be a default, and an absent value already means
     * the visitor has not opted out.
     */
    public async _getIsOptedOut(): Promise<boolean> {
        const req = await this._getData<number>(UserStorageKey.isOptedOut, {
            preferredStores: synchronizedKeyStores,
        })
        return !!req.unwrapOrNull()
    }

    /**
     * Why this browser is opted out, or `null` when it is not. An unrecognised value reads as an
     * explicit request, because a marker of unknown origin must never be undone by consent.
     */
    public async _getOptedOutSource(): Promise<OptOutSource | null> {
        const req = await this._getData<number>(UserStorageKey.isOptedOut, {
            preferredStores: synchronizedKeyStores,
        })
        const stored = req.unwrapOrNull()
        if (!stored) {
            return null
        }

        return stored === OptOutSource.CONSENT_DENIED ? OptOutSource.CONSENT_DENIED : OptOutSource.EXPLICIT_REQUEST
    }

    /**
     * Written domain-wide for the same reason as the deleted state. Clearing it is the only way
     * back from a withdrawal, so anything that lets a visitor opt in again goes through here.
     */
    public async _setIsOptedOut(optedOut: true, source: OptOutSource): Promise<boolean>
    public async _setIsOptedOut(optedOut: false): Promise<boolean>
    public async _setIsOptedOut(optedOut: boolean, source?: OptOutSource): Promise<boolean> {
        return this._setHaltMarker(UserStorageKey.isOptedOut, optedOut ? source! : 0)
    }

    /**
     * Writes a halt marker to the cookie, IndexedDB and memory, and reports whether it was stored
     * durably. Memory always accepts a write but is gone on the next load, so it cannot keep a
     * browser halted; only the cookie or IndexedDB can.
     *
     * Setting a marker always reaches memory, so this page halts even when nothing durable took it.
     * Clearing one reaches memory only once a durable store took it: a cleared marker held only in
     * memory reads as the newest value, and this page would report the browser as not halted while
     * the next load still is.
     */
    private async _setHaltMarker(key: UserStorageKey, value: 0 | OptOutSource | 1): Promise<boolean> {
        if (value !== 0) {
            const [durableWrite] = await Promise.all([
                this._setData(key, value, { preferredStores: durableSynchronizedKeyStores }),
                this._setData(key, value, { preferredStores: memoryStoreOnly }),
            ])
            return durableWrite.isOk()
        }

        const durableWrite = await this._setData(key, value, { preferredStores: durableSynchronizedKeyStores })
        if (durableWrite.isErr()) {
            return false
        }

        await this._setData(key, value, { preferredStores: memoryStoreOnly })
        return true
    }

    /**
     * Whether nothing may be tracked or subscribed: the visitor opted out or asked to be deleted, or
     * the site reported on this page that consent is pending or denied.
     */
    public async _getIsTrackingHalted(): Promise<boolean> {
        if (this._isTrackingHaltedForThisPage) {
            return true
        }
        const [isDeleted, isOptedOut] = await Promise.all([this._getIsInDeletedState(), this._getIsOptedOut()])
        return isDeleted || isOptedOut
    }

    public _haltTrackingForThisPage(): void {
        this._isTrackingHaltedForThisPage = true
    }

    public _resumeTrackingForThisPage(): void {
        this._isTrackingHaltedForThisPage = false
    }

    public async _getReportedSubscriptionFingerprint(): Promise<string | null> {
        const req = await this._getData<string>(UserStorageKey.reportedSubscriptionFingerprint, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.unwrapOrNull()
    }

    public async _setReportedSubscriptionFingerprint(fingerprint: string): Promise<boolean> {
        const req = await this._setData(UserStorageKey.reportedSubscriptionFingerprint, fingerprint, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.isOk()
    }

    public async _getLastNotificationPermission(): Promise<NotificationPermission | null> {
        const req = await this._getData<NotificationPermission>(UserStorageKey.lastNotificationPermission, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.unwrapOrNull()
    }

    public async _setLastNotificationPermission(permission: NotificationPermission): Promise<boolean> {
        const req = await this._setData(UserStorageKey.lastNotificationPermission, permission, {
            preferredStores: idbAndMemOnlyStores,
        })
        return req.isOk()
    }

    protected _removeLegacyKey(key: (typeof LegacyStorageKey)[keyof typeof LegacyStorageKey]) {
        this._logger.debug(`!! Legacy key removal DISABLED !! key: ${key}`)
        // @todo - re-enable prior to release
        // this._legacyStorage
        //     ._removeData(key)
        //     .catch((err) => this._logger.warn(`Unable to remove legacy key: ${key}`, err?.message ?? err))
        //     .then((res) => {
        //         if (res && res.isErr()) {
        //             this._logger.warn(`Unable to remove legacy key: ${key}`, res.getErr()!.message)
        //         }
        //     })
    }
}
